import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  Plus,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminUsersGetAdminUsersQueryKey,
  getAdminUsersGetAdminUsersIdGuidSessionsQueryKey,
  useAdminUsersDeleteAdminUsersIdGuid,
  useAdminUsersDeleteAdminUsersIdGuidSessions,
  useAdminUsersGetAdminUsers,
  useAdminUsersGetAdminUsersIdGuidSessions,
  useAdminUsersPostAdminUsers,
  useAdminUsersPostAdminUsersIdGuidActivate,
  useAdminUsersPostAdminUsersIdGuidDeactivate,
  useAdminUsersPutAdminUsersIdGuid,
} from '@/lib/api/admin-users/admin-users';
import { AdminRole, type AdminUserDto, type CreateAdminUserResult } from '@/lib/api/schemas';
import {
  AllowedEnvironmentsField,
  fromAllowedEnvironments,
  toAllowedEnvironmentsArray,
  type AllowedEnvironmentsValue,
} from '@/components/AllowedEnvironmentsField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useAuth, useHasRole } from '@/lib/auth/useAuth';

type DialogState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; user: AdminUserDto }
  | { kind: 'delete'; user: AdminUserDto }
  | { kind: 'sessions'; user: AdminUserDto };

export default function UsersPage() {
  const canMutate = useHasRole('SuperAdmin');
  const { user: currentUser } = useAuth();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [page, setPage] = useState(1);

  const query = useAdminUsersGetAdminUsers({ page, limit: 50 });

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Admin users</h1>
          <p className="text-sm text-muted-foreground">
            Dashboard access. Only SuperAdmins can create, change roles, deactivate, or delete.
          </p>
        </div>
        {canMutate && (
          <Button className="gap-2" onClick={() => setDialog({ kind: 'create' })}>
            <Plus className="h-4 w-4" />
            Invite user
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="p-0">
          {query.isError ? (
            <div className="p-6 text-sm text-destructive">
              {(query.error as Error)?.message ?? 'Could not load admin users.'}
            </div>
          ) : query.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : !query.data || query.data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No admin users yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Email</th>
                    <th className="p-3 font-medium">Role</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Last login</th>
                    <th className="p-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {query.data.items.map((u) => (
                    <tr key={u.id}>
                      <td className="p-3 font-medium">{u.fullName}</td>
                      <td className="p-3 text-xs text-muted-foreground">{u.email}</td>
                      <td className="p-3">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="p-3">
                        {u.isActive ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'Never'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setDialog({ kind: 'sessions', user: u })}>
                            <Users className="h-3.5 w-3.5" />
                          </Button>
                          {canMutate && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setDialog({ kind: 'edit', user: u })}>
                                Edit
                              </Button>
                              {u.id !== currentUser?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => setDialog({ kind: 'delete', user: u })}
                                >
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {query.data && query.data.total > query.data.limit && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              {query.data.items.length} of {query.data.total}
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {dialog.kind === 'create' && <CreateUserDialog onClose={() => setDialog({ kind: 'none' })} />}
      {dialog.kind === 'edit' && (
        <EditUserDialog user={dialog.user} onClose={() => setDialog({ kind: 'none' })} />
      )}
      {dialog.kind === 'delete' && (
        <DeleteUserDialog user={dialog.user} onClose={() => setDialog({ kind: 'none' })} />
      )}
      {dialog.kind === 'sessions' && (
        <SessionsDialog user={dialog.user} canMutate={canMutate} onClose={() => setDialog({ kind: 'none' })} />
      )}
    </section>
  );
}

function RoleBadge({ role }: { role: AdminUserDto['role'] }) {
  if (role === AdminRole.SuperAdmin) return <Badge variant="destructive">SuperAdmin</Badge>;
  if (role === AdminRole.Admin) return <Badge variant="default">Admin</Badge>;
  return <Badge variant="outline">Viewer</Badge>;
}

// ------ Create user (returns temp password ONCE) ------

const CreateSchema = z.object({
  email: z.string().email('Enter a valid email.'),
  fullName: z.string().min(1).max(200),
  role: z.enum(['Viewer', 'Admin', 'SuperAdmin']),
});

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<CreateAdminUserResult | null>(null);
  const [visible, setVisible] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  // New admins default to Production + Sandbox (matches the bootstrap
  // SuperAdmin shape). Caller can untick either before submit.
  const [allowedEnv, setAllowedEnv] = useState<AllowedEnvironmentsValue>({
    production: true,
    sandbox: true,
  });
  const [allowedEnvError, setAllowedEnvError] = useState<string | null>(null);

  type Form = z.infer<typeof CreateSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(CreateSchema), defaultValues: { email: '', fullName: '', role: 'Viewer' } });

  const mutation = useAdminUsersPostAdminUsers({
    mutation: {
      onSuccess: async (r) => {
        setResult(r);
        await queryClient.invalidateQueries({ queryKey: getAdminUsersGetAdminUsersQueryKey() });
      },
      onError: (err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        toast.error(status === 409 ? 'That email already has an admin account.' : 'Could not invite user.');
      },
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!allowedEnv.production && !allowedEnv.sandbox) {
      setAllowedEnvError('Pick at least one environment.');
      return;
    }
    setAllowedEnvError(null);
    // Phase B will add `allowedEnvironments` to the CreateAdminUserRequest
    // shape — pass it now via a loose cast so the wire payload is ready
    // the moment the OpenAPI regenerates. Today the backend ignores it.
    const payload = {
      ...values,
      allowedEnvironments: toAllowedEnvironmentsArray(allowedEnv),
    } as unknown as typeof values;
    await mutation.mutateAsync({ data: payload });
  });

  const close = () => {
    if (result && !acknowledged) return;
    onClose();
  };

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.temporaryPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={(next) => !next && close()}>
      <DialogContent>
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Invite admin user
              </DialogTitle>
              <DialogDescription>
                A temporary password will be shown once after creation. Share it with the invitee over a secure channel.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="fullName">Full name</Label>
                <Input id="fullName" {...register('fullName')} aria-invalid={!!errors.fullName} />
                {errors.fullName && <p className="text-xs text-destructive">{errors.fullName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" {...register('email')} aria-invalid={!!errors.email} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select id="role" {...register('role')}>
                  <option value="Viewer">Viewer — read-only</option>
                  <option value="Admin">Admin — manage clients &amp; secrets</option>
                  <option value="SuperAdmin">SuperAdmin — full control</option>
                </Select>
              </div>
              <AllowedEnvironmentsField
                value={allowedEnv}
                onChange={(next) => {
                  setAllowedEnv(next);
                  if (next.production || next.sandbox) setAllowedEnvError(null);
                }}
                description="Which environments this admin can sign in to. Pick at least one."
                error={allowedEnvError ?? undefined}
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || mutation.isPending}>
                  {mutation.isPending ? 'Inviting…' : 'Invite'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Temporary password — shown once</DialogTitle>
              <DialogDescription>
                {result.user.email} should sign in with this and then change it. It won&apos;t be displayed again.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted p-3">
                <code className="flex-1 break-all font-mono text-xs">
                  {visible ? result.temporaryPassword : '•'.repeat(result.temporaryPassword.length)}
                </code>
                <Button size="icon" variant="ghost" aria-label={visible ? 'Hide' : 'Show'} onClick={() => setVisible((v) => !v)}>
                  {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="icon" variant="ghost" aria-label="Copy" onClick={copy}>
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                I&apos;ve sent this to the invitee over a secure channel.
              </label>
            </div>
            <DialogFooter>
              <Button onClick={onClose} disabled={!acknowledged}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------ Edit user (name + role + activation toggle) ------

const EditSchema = z.object({
  fullName: z.string().min(1).max(200),
  role: z.enum(['Viewer', 'Admin', 'SuperAdmin']),
});

function EditUserDialog({ user, onClose }: { user: AdminUserDto; onClose: () => void }) {
  const queryClient = useQueryClient();
  type Form = z.infer<typeof EditSchema>;

  // Read the user's current entitlement defensively — Phase B will add
  // `allowedEnvironments` to AdminUserDto; until then we fall back to
  // `Both` (admins span both envs by default).
  const initialAllowed = (user as { allowedEnvironments?: 'None' | 'Sandbox' | 'Production' | 'Both' })
    .allowedEnvironments;
  const [allowedEnv, setAllowedEnv] = useState<AllowedEnvironmentsValue>(
    fromAllowedEnvironments(initialAllowed ?? 'Both'),
  );
  const [allowedEnvError, setAllowedEnvError] = useState<string | null>(null);
  const initialAllowedSnapshot = JSON.stringify(fromAllowedEnvironments(initialAllowed ?? 'Both'));
  const allowedDirty = JSON.stringify(allowedEnv) !== initialAllowedSnapshot;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<Form>({
    resolver: zodResolver(EditSchema),
    defaultValues: { fullName: user.fullName, role: user.role },
  });

  const updateMutation = useAdminUsersPutAdminUsersIdGuid({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getAdminUsersGetAdminUsersQueryKey() });
        toast.success('User updated.');
        onClose();
      },
      onError: (err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        toast.error(status === 409 ? 'Cannot demote the last SuperAdmin.' : 'Could not update user.');
      },
    },
  });

  const deactivateMutation = useAdminUsersPostAdminUsersIdGuidDeactivate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getAdminUsersGetAdminUsersQueryKey() });
        toast.success('User deactivated.');
        onClose();
      },
    },
  });
  const activateMutation = useAdminUsersPostAdminUsersIdGuidActivate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getAdminUsersGetAdminUsersQueryKey() });
        toast.success('User activated.');
        onClose();
      },
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!allowedEnv.production && !allowedEnv.sandbox) {
      setAllowedEnvError('Pick at least one environment.');
      return;
    }
    setAllowedEnvError(null);
    // See CreateUserDialog — Phase B extends the wire shape. Loose-cast
    // so the OpenAPI regen can drop straight in.
    const payload = {
      ...values,
      allowedEnvironments: toAllowedEnvironmentsArray(allowedEnv),
    } as unknown as typeof values;
    await updateMutation.mutateAsync({ id: user.id, data: payload });
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {user.email}</DialogTitle>
          <DialogDescription>Role changes take effect on the user&apos;s next request.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="edit-fullName">Full name</Label>
            <Input id="edit-fullName" {...register('fullName')} />
            {errors.fullName && <p className="text-xs text-destructive">{errors.fullName.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-role">Role</Label>
            <Select id="edit-role" {...register('role')}>
              <option value="Viewer">Viewer</option>
              <option value="Admin">Admin</option>
              <option value="SuperAdmin">SuperAdmin</option>
            </Select>
          </div>

          <AllowedEnvironmentsField
            value={allowedEnv}
            onChange={(next) => {
              setAllowedEnv(next);
              if (next.production || next.sandbox) setAllowedEnvError(null);
            }}
            description="Which environments this admin can sign in to."
            error={allowedEnvError ?? undefined}
          />

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <span>
              Account is <strong>{user.isActive ? 'active' : 'inactive'}</strong>.
            </span>
            {user.isActive ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => deactivateMutation.mutate({ id: user.id })}
                disabled={deactivateMutation.isPending}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => activateMutation.mutate({ id: user.id })}
                disabled={activateMutation.isPending}
              >
                Reactivate
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={(!isDirty && !allowedDirty) || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------ Delete confirm ------

function DeleteUserDialog({ user, onClose }: { user: AdminUserDto; onClose: () => void }) {
  const queryClient = useQueryClient();
  const mutation = useAdminUsersDeleteAdminUsersIdGuid({
    mutation: {
      onSuccess: async () => {
        toast.success('User deleted.');
        await queryClient.invalidateQueries({ queryKey: getAdminUsersGetAdminUsersQueryKey() });
        onClose();
      },
      onError: (err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        toast.error(status === 409 ? 'Cannot delete the last SuperAdmin.' : 'Could not delete user.');
      },
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Delete admin user
          </DialogTitle>
          <DialogDescription>
            {user.fullName} ({user.email}) will lose dashboard access immediately and their sessions will be revoked.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate({ id: user.id })} disabled={mutation.isPending}>
            {mutation.isPending ? 'Deleting…' : 'Delete user'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------ Sessions list + revoke-all ------

function SessionsDialog({
  user,
  canMutate,
  onClose,
}: {
  user: AdminUserDto;
  canMutate: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdminUsersGetAdminUsersIdGuidSessions(user.id);
  const revokeAll = useAdminUsersDeleteAdminUsersIdGuidSessions({
    mutation: {
      onSuccess: async (count) => {
        toast.success(`Revoked ${count ?? 0} session(s).`);
        await queryClient.invalidateQueries({
          queryKey: getAdminUsersGetAdminUsersIdGuidSessionsQueryKey(user.id),
        });
      },
      onError: () => toast.error('Could not revoke sessions.'),
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Sessions · {user.email}</DialogTitle>
          <DialogDescription>
            Shows the 100 most recent refresh sessions — active and revoked.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sessions on file.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="border-b border-border bg-muted/30 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-2">Created</th>
                  <th className="p-2">IP</th>
                  <th className="p-2">User agent</th>
                  <th className="p-2">Expires</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((s) => (
                  <tr key={s.id}>
                    <td className="p-2 tabular-nums">{new Date(s.createdAt).toLocaleString()}</td>
                    <td className="p-2 font-mono">{s.ipAddress ?? '—'}</td>
                    <td className="max-w-[20ch] truncate p-2 font-mono">{s.userAgent ?? '—'}</td>
                    <td className="p-2 tabular-nums">{new Date(s.expiresAt).toLocaleString()}</td>
                    <td className="p-2">
                      {s.revokedAt ? <Badge variant="outline">Revoked</Badge> : <Badge variant="success">Active</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          {canMutate && (
            <Button
              variant="destructive"
              className="gap-2"
              onClick={() => revokeAll.mutate({ id: user.id })}
              disabled={revokeAll.isPending}
            >
              <UserMinus className="h-4 w-4" />
              {revokeAll.isPending ? 'Revoking…' : 'Revoke all sessions'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
